import './PrivacyPage.css'

function PrivacyPage() {
  const lastUpdated = 'January 24, 2026'

  return (
    <div className="privacy-page">
      <section className="page-hero">
        <div className="container">
          <h1>Privacy Policy</h1>
          <p className="page-hero-subtitle">
            Your privacy is important to us. This policy explains how we collect, use, and protect your information.
          </p>
        </div>
      </section>

      <section className="privacy-content">
        <div className="container">
          <div className="privacy-intro">
            <p className="privacy-updated">
              <strong>Last Updated:</strong> {lastUpdated}
            </p>
            <p>
              Laboratory Specialists ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
              explains how we collect, use, disclose, and safeguard your information when you visit our website 
              and use our services.
            </p>
          </div>

          <div className="privacy-section">
            <h2>1. Information We Collect</h2>
            
            <h3>1.1 Information You Provide to Us</h3>
            <p>We may collect information that you voluntarily provide to us when you:</p>
            <ul>
              <li>Fill out contact forms or booking forms on our website</li>
              <li>Schedule consultations or appointments</li>
              <li>Subscribe to our newsletter or marketing communications</li>
              <li>Request information about our products or services</li>
              <li>Contact us via email, phone, or other communication methods</li>
            </ul>
            <p>This information may include:</p>
            <ul>
              <li>Name and contact information (email address, phone number, mailing address)</li>
              <li>Company or institution name</li>
              <li>Job title or role</li>
              <li>Information about your laboratory equipment needs</li>
              <li>Any other information you choose to provide</li>
            </ul>

            <h3>1.2 Automatically Collected Information</h3>
            <p>When you visit our website, we may automatically collect certain information about your device and 
            browsing behavior, including:</p>
            <ul>
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Operating system</li>
              <li>Pages you visit and time spent on pages</li>
              <li>Referring website addresses</li>
              <li>Date and time of your visit</li>
              <li>Device identifiers</li>
            </ul>
            <p>We collect this information using cookies, web beacons, and similar tracking technologies.</p>

            <h3>1.3 Third-Party Information</h3>
            <p>We may receive information about you from third-party sources, such as:</p>
            <ul>
              <li>Calendly or other scheduling platforms when you book appointments</li>
              <li>Analytics providers (e.g., Google Analytics)</li>
              <li>Social media platforms if you interact with us through social media</li>
            </ul>
          </div>

          <div className="privacy-section">
            <h2>2. How We Use Your Information</h2>
            <p>We use the information we collect for the following purposes:</p>
            <ul>
              <li><strong>To Provide Services:</strong> To respond to your inquiries, schedule consultations, 
              provide quotes, and deliver our products and services</li>
              <li><strong>To Communicate:</strong> To send you information about our services, respond to your 
              questions, and provide customer support</li>
              <li><strong>To Improve Our Services:</strong> To analyze website usage, understand user preferences, 
              and improve our website and services</li>
              <li><strong>For Marketing:</strong> To send you promotional materials, newsletters, and other 
              marketing communications (with your consent where required)</li>
              <li><strong>For Legal Compliance:</strong> To comply with applicable laws, regulations, and legal 
              processes</li>
              <li><strong>To Protect Rights:</strong> To protect our rights, property, and safety, as well as 
              those of our users and others</li>
            </ul>
          </div>

          <div className="privacy-section">
            <h2>3. How We Share Your Information</h2>
            <p>We do not sell your personal information. We may share your information in the following circumstances:</p>
            
            <h3>3.1 Service Providers</h3>
            <p>We may share your information with third-party service providers who perform services on our behalf, 
            such as:</p>
            <ul>
              <li>Website hosting and maintenance</li>
              <li>Email service providers</li>
              <li>Analytics and marketing tools</li>
              <li>Payment processors (if applicable)</li>
              <li>Scheduling platforms (e.g., Calendly)</li>
            </ul>
            <p>These service providers are contractually obligated to protect your information and use it only for 
            the purposes we specify.</p>

            <h3>3.2 Business Transfers</h3>
            <p>If we are involved in a merger, acquisition, or sale of assets, your information may be transferred 
            as part of that transaction. We will notify you of any such change in ownership or control.</p>

            <h3>3.3 Legal Requirements</h3>
            <p>We may disclose your information if required to do so by law or in response to valid requests by 
            public authorities (e.g., court orders, subpoenas).</p>

            <h3>3.4 With Your Consent</h3>
            <p>We may share your information with third parties when you have given us explicit consent to do so.</p>
          </div>

          <div className="privacy-section">
            <h2>4. Cookies and Tracking Technologies</h2>
            <p>We use cookies and similar tracking technologies to collect and store information about your 
            preferences and browsing behavior. Cookies are small text files placed on your device when you visit 
            our website.</p>
            
            <h3>Types of Cookies We Use:</h3>
            <ul>
              <li><strong>Essential Cookies:</strong> Required for the website to function properly</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our website</li>
            </ul>
            
            <p>When you first visit our site, a banner lets you accept or decline non-essential analytics
            cookies. Analytics cookies are not set unless you accept. You can also control cookies through your
            browser settings; however, disabling cookies may limit your ability to use certain features of our
            website.</p>
          </div>

          <div className="privacy-section">
            <h2>5. Data Security</h2>
            <p>We implement appropriate technical and organizational security measures to protect your personal 
            information against unauthorized access, alteration, disclosure, or destruction. These measures include:</p>
            <ul>
              <li>Encryption of data in transit and at rest</li>
              <li>Secure server infrastructure</li>
              <li>Regular security assessments</li>
              <li>Access controls and employee training</li>
            </ul>
            <p>However, no method of transmission over the Internet or electronic storage is 100% secure. While 
            we strive to protect your information, we cannot guarantee absolute security.</p>
          </div>

          <div className="privacy-section">
            <h2>6. Your Rights and Choices</h2>
            <p>Depending on your location, you may have certain rights regarding your personal information, including:</p>
            <ul>
              <li><strong>Access:</strong> Request access to the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
              <li><strong>Data Portability:</strong> Request a copy of your data in a portable format</li>
              <li><strong>Objection:</strong> Object to certain processing of your information</li>
            </ul>
            <p>To exercise these rights, please contact us using the information provided in the "Contact Us" 
            section below.</p>
          </div>

          <div className="privacy-section">
            <h2>7. Children's Privacy</h2>
            <p>Our website and services are not intended for children under the age of 13. We do not knowingly 
            collect personal information from children under 13. If you believe we have collected information 
            from a child under 13, please contact us immediately, and we will take steps to delete such information.</p>
          </div>

          <div className="privacy-section">
            <h2>8. International Data Transfers</h2>
            <p>If you are located outside the United States, please note that we may transfer your information 
            to and process it in the United States or other countries. By using our website and services, you 
            consent to the transfer of your information to these countries, which may have different data 
            protection laws than your country of residence.</p>
          </div>

          <div className="privacy-section">
            <h2>9. California Privacy Rights</h2>
            <p>If you are a California resident, you have additional rights under the California Consumer Privacy 
            Act (CCPA), including:</p>
            <ul>
              <li>The right to know what personal information we collect, use, and share</li>
              <li>The right to delete your personal information</li>
              <li>The right to opt-out of the sale of personal information (we do not sell personal information)</li>
              <li>The right to non-discrimination for exercising your privacy rights</li>
            </ul>
            <p>To exercise your California privacy rights, please contact us using the information provided below.</p>
          </div>

          <div className="privacy-section">
            <h2>10. European Privacy Rights (GDPR)</h2>
            <p>If you are located in the European Economic Area (EEA), you have certain rights under the General 
            Data Protection Regulation (GDPR), including:</p>
            <ul>
              <li>Right of access to your personal data</li>
              <li>Right to rectification of inaccurate data</li>
              <li>Right to erasure ("right to be forgotten")</li>
              <li>Right to restrict processing</li>
              <li>Right to data portability</li>
              <li>Right to object to processing</li>
              <li>Right to withdraw consent</li>
            </ul>
            <p>To exercise your GDPR rights, please contact us using the information provided below.</p>
          </div>

          <div className="privacy-section">
            <h2>11. Data Retention</h2>
            <p>We retain your personal information for as long as necessary to fulfill the purposes outlined in 
            this Privacy Policy, unless a longer retention period is required or permitted by law. When we no 
            longer need your information, we will securely delete or anonymize it.</p>
          </div>

          <div className="privacy-section">
            <h2>12. Third-Party Links</h2>
            <p>Our website may contain links to third-party websites or services. We are not responsible for the 
            privacy practices of these third parties. We encourage you to read the privacy policies of any 
            third-party websites you visit.</p>
          </div>

          <div className="privacy-section">
            <h2>13. Changes to This Privacy Policy</h2>
            <p>We may update this Privacy Policy from time to time to reflect changes in our practices or for 
            other operational, legal, or regulatory reasons. We will notify you of any material changes by posting 
            the updated policy on our website and updating the "Last Updated" date. Your continued use of our 
            website after such changes constitutes your acceptance of the updated Privacy Policy.</p>
          </div>

          <div className="privacy-section">
            <h2>14. Contact Us</h2>
            <p>If you have any questions, concerns, or requests regarding this Privacy Policy or our data 
            practices, please contact us:</p>
            <div className="privacy-contact">
              <p>
                <strong>Laboratory Specialists</strong><br />
                Email: <a href="mailto:info@labspecialists.com">info@labspecialists.com</a><br />
                Text or Call: <a href="tel:+15416589374">(541) 658-9374</a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default PrivacyPage
